import * as React from 'react';
import { pickBy } from 'lodash-es';
import { useLocation, match, useRouteMatch } from 'react-router';
import * as qs from 'query-string';
import { LogPerformance } from '../utils';
import AppLogger from '../../../utils/logging';
const logger = AppLogger.createLogger();
/*
 For reasons I don't fully understand, webpack transpiles this to try to import from the
 .mjs compiled version of this package which throws an error in the browser env
 unless the commonjs compiled version is explicitly specified
*/
import { Base64 } from 'js-base64';

interface WindowWithPreLoadHooks extends Window {
    // We will receive this data from backend inserted to html, like <scrip>__PRELOADED_API_HOOKS = ...</script>
    // The value of this is a base64 encoded stringified state object
    __PRELOADED_API_HOOKS__?: string;

    registeredHookId: {
        [index: string]: boolean;
        // We will use this only on frontend part, to validate that each ApiHook have uniq id
    };
}

declare var window: WindowWithPreLoadHooks;
if (typeof window !== 'undefined') {
    window.registeredHookId = {};
}

export type ParsedQuery = Record<string, string | string[] | null | undefined>;

export type DataFetchStatus = 'success' | 'not_found' | 'error' | 'permanent_redirect';
export type DataFetchErrorResponse = {
    // this type is used in promise which returned after endpoint fetch
    apiHookStatus: DataFetchStatus;
    details?: string;
};
export type DataFetchStatusFull = {
    // this type is used to return data fetch status from hook
    status: DataFetchStatus;
    details?: string;
};
export type DataFetchResult<T> = T | DataFetchErrorResponse;

/**
 * Function that takes the route match parameters and the request query parameters
 * as a dictionary and returns the desired data
 */
type DataFetchFunction<T, P extends { [K in keyof P]?: string }> = (match: match<P>, search: ParsedQuery) => T;

/**
 * Interface for storing precomputed results from the data fetch along with a
 * unique id to identify the results stored in the page markup
 */
type DataFetchFunctionProps<T> = {
    uniqId?: string;
};

/**
 * The data fetching function for fetching necessary page data, exposes interface for storing
 * pre-computed results of the data fetch function
 */
type AsyncDataFetchFunction<T, P extends { [K in keyof P]?: string } = {}> = DataFetchFunction<Promise<DataFetchResult<T>>, P> &
    DataFetchFunctionProps<T>;

type PreloadedData<T> = { data: T | null; status?: DataFetchStatusFull };

/**
 * The function that uses the react state hook to fetch and store API results, extended with
 * the interface necessary for the server to fetch data and preload the results for SSR
 *
 */
type SSREffectHook<T, P extends { [K in keyof P]?: string } = {}> = ((
    dependencies?: any[]
) => [T | null | undefined, boolean, DataFetchStatusFull | undefined]) &
    DataFetchFunctionProps<T> & { api?: AsyncDataFetchFunction<T, P> };

/**
 * Describes a component with API hook calls that need to run on SSR
 */
export type ComponentWithSSRHooks = React.FC & { apiHooks: SSREffectHook<any, any>[] };

/**
 * Creates a custom hook given a data fetch function and a unique ID. The returned function
 * will do the following:
 *
 * On client side:
 *   - React.useEffect that will either get preloaded data embedded in markup or call the
 *     data fetch method
 *
 * On server side:
 *   - Provide an interface for triggering the data fetch method asynchronously and storing
 *     the results on the returned function
 *
 * @param api - The underlying data fetch function
 * @param uniqId - See DataFetchFunction.uniqId
 */
export const createHookFromAPI = <T, P = {}>(api: AsyncDataFetchFunction<T, P>, uniqId: string) => {
    if (typeof window !== 'undefined') {
        if (window.registeredHookId[uniqId]) {
            logger.error(
                `
Important error! U create two different ssr-hook with the same uniqId=%c ${uniqId} %c 
\nU MUST fix it! otherwise server side rendering will work wrong
                `,
                'font-weight: bold;background: #0003',
                'all: unset'
            );
        }
        window.registeredHookId[uniqId] = true;
    }

    // This is the hook used by the component. Note that in the SSR scenario,
    // the caller must trigger the data fetch using the hook.api interface provided
    // both the hook.api and hook function have the same method signatures for consistency
    const hook: SSREffectHook<T, P> = (_dependencies: any[] = []) => {
        let preloadValue: PreloadedData<T> | undefined;
        if (typeof window !== 'undefined') {
            // The value stored on the window object is stringified and base64 encoded so we need
            // to decode both before we arrive at the object
            const encodedValue = window.__PRELOADED_API_HOOKS__;
            if (encodedValue) {
                preloadValue = JSON.parse(Base64.decode(encodedValue))[uniqId];
            }
        } else if (typeof global !== 'undefined') {
            // @ts-ignore
            preloadValue = global.preloadedHooksState[uniqId];
        }

        const preloadData = preloadValue ? preloadValue.data : undefined;
        const [data, setData] = React.useState<T | null | undefined>(preloadData);
        const [isLoading, setIsLoading] = React.useState<boolean>(!preloadData);
        const [dataStatus, setDataStatus] = React.useState<DataFetchStatusFull | undefined>(preloadValue ? preloadValue.status : undefined);

        const search = pickBy(qs.parse(useLocation().search)) as ParsedQuery;
        const match = useRouteMatch<P>();

        if (!match) {
            logger.error(
                `
ApiHook error! No implicit or explicit react-router match was found while executing api hook
\n any preloaded data will be returnede but no new data will be fetched.
                `,
                'font-weight: bold;background: #0003',
                'all: unset'
            );
            return [data, isLoading, dataStatus];
        }

        // Keep track of whether or not the component using this hook has already been mounted
        // we keep track of this because we only want to return preloaded data once, when
        // the component is first mounted
        let mountedRef = React.useRef(false);

        // The effect function will execute once for the component on the client side like
        // the react component lifecycle method componentDidMount
        React.useEffect(() => {
            // If we detect preloaded data, skip any subsequent API calls
            // this value should be preloaded by the react render server, but only
            // for the first client-side render call
            if (!mountedRef.current) {
                mountedRef.current = true;
                if (preloadData) {
                    // Remove after use
                    if (typeof window !== 'undefined') {
                        delete (window.__PRELOADED_API_HOOKS__ as string)[uniqId];
                    }
                    return;
                }
            }

            setIsLoading(true);

            // Call the underlying API if no preloaded state was found
            LogPerformance('Call API ApiHookCreator');

            api(match, search)
                .then((res) => {
                    setData(res as T);
                    setIsLoading(false);
                    setDataStatus({ status: 'success' });
                })
                .catch((res) => {
                    setData(null);
                    setIsLoading(false);

                    // If an explicit apiHookStatus value was passed w/ the
                    // error, set it as the apiHookStatus, otherwise just assume
                    // an error. The idea is for the caller to handle the error and
                    // determine the status after parsing the original error
                    if (res.apiHookStatus) {
                        setDataStatus({
                            status: res.apiHookStatus,
                            details: res.details,
                        });
                    } else {
                        setDataStatus({ status: 'error' });
                    }
                });
        }, _dependencies);

        return [data, isLoading, dataStatus];
    };

    hook.api = api;
    hook.uniqId = uniqId;
    return hook;
};
